import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
  Checkbox,
  ListItemText,
  Chip,
  Tooltip,
  FormControlLabel,
  Switch,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import {
  collection,
  query,
  where,
  orderBy,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, auth, functions } from '../firebase';
import LoadingLockScreen from './LoadingLockScreen';
import { useCompanyUsers } from '../hooks/useCompanyUsers';
import { useFirestoreQuery } from '../hooks/useFirestoreQuery';
import {
  normalizeExternalUrl,
  openExternalLinkReuseOrFocus,
  openExternalLinkNewTab,
} from '../lib/externalLinkWindows';
import { appendTicketLog } from '../lib/ticketLogs';

const CATEGORY_PRESETS = ['CRM', 'Finance', 'Back-Office', 'HR', 'IT', 'Operations', 'Other'];

function groupLinksByCategory(items) {
  const map = new Map();
  items.forEach((item) => {
    const key = (item.category || 'Uncategorized').trim() || 'Uncategorized';
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(item);
  });
  return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
}

function getAssignedIds(link) {
  return Array.isArray(link.assignedUsers) ? link.assignedUsers : [];
}

function firestoreLinksFetchErrorMessage(err) {
  const msg = err?.message || '';
  if (msg.includes('building') || err?.code === 'failed-precondition') {
    return 'Firestore is still building a required index (often a few minutes). Refresh shortly, or check Indexes in the Firebase console.';
  }
  if (msg.includes('index')) {
    return 'This query needs a Firestore composite index. Use the link from the browser console, or deploy firestore.indexes.json.';
  }
  return msg || 'Failed to load links.';
}

function assignmentTooltip(link, companyUsers) {
  const ids = getAssignedIds(link);
  if (ids.length === 0) {
    return 'No agents assigned — only company admins see this link in the list.';
  }
  const byUid = new Map(companyUsers.map((u) => [u.uid, u]));
  return ids
    .map((id) => {
      const u = byUid.get(id);
      if (u) return u.displayName || u.email || id;
      return id;
    })
    .join(', ');
}

function parseDate(value) {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function healthChip(link) {
  const hb = parseDate(link.heartbeatAt);
  const status = link.liveStatus || 'unknown';
  if (status === 'down') return { label: 'DOWN', color: 'error' };
  if (status === 'degraded') return { label: 'DEGRADED', color: 'warning' };
  if (status === 'live') {
    if (!hb) return { label: 'LIVE', color: 'success' };
    const tenMinutes = 10 * 60 * 1000;
    if (Date.now() - hb.getTime() <= tenMinutes) return { label: 'LIVE', color: 'success' };
    return { label: 'STALE', color: 'warning' };
  }
  return { label: 'UNKNOWN', color: 'default' };
}

const ExternalLinks = ({ userData }) => {
  const [fetchError, setFetchError] = useState(null);
  const [splashOnce, setSplashOnce] = useState(true);

  const [addOpen, setAddOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [newCategory, setNewCategory] = useState('CRM');
  const [assignedUserIds, setAssignedUserIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [checkingLinkId, setCheckingLinkId] = useState('');

  const companyId = userData?.companyId;
  const isCompanyAdmin = userData?.role === 'company_admin';
  const uid = auth.currentUser?.uid;

  const linksQuery = useMemo(() => {
    if (!companyId) return null;
    if (!isCompanyAdmin && !uid) return null;
    const constraints = [where('companyId', '==', companyId)];
    if (!isCompanyAdmin) {
      constraints.push(where('assignedUsers', 'array-contains', uid));
    }
    constraints.push(orderBy('createdAt', 'desc'));
    return query(collection(db, 'links'), ...constraints);
  }, [companyId, isCompanyAdmin, uid]);

  const {
    data: links,
    loading,
    error: linksQueryError,
  } = useFirestoreQuery(linksQuery, {
    enabled: Boolean(companyId && (isCompanyAdmin || uid)),
  });

  const { users: companyUsersRaw } = useCompanyUsers(companyId, {
    enabled: Boolean(companyId && isCompanyAdmin),
  });

  const companyUsers = useMemo(
    () =>
      companyUsersRaw.map((u) => ({
        uid: u.uid,
        displayName: u.displayName,
        email: u.email,
        role: u.role,
      })),
    [companyUsersRaw],
  );

  useEffect(() => {
    if (!loading) setSplashOnce(false);
  }, [loading]);

  useEffect(() => {
    if (linksQueryError) {
      setFetchError(firestoreLinksFetchErrorMessage(linksQueryError));
    } else {
      setFetchError(null);
    }
  }, [linksQueryError]);

  const grouped = useMemo(() => groupLinksByCategory(links), [links]);

  const resetAddForm = () => {
    setNewTitle('');
    setNewUrl('');
    setNewCategory('CRM');
    setAssignedUserIds([]);
    setFormError(null);
  };

  const handleAddClose = () => {
    if (saving) return;
    setAddOpen(false);
    resetAddForm();
  };

  const handleAddSubmit = async () => {
    setFormError(null);
    const title = newTitle.trim();
    const url = normalizeExternalUrl(newUrl);
    const category = newCategory.trim();

    if (!title) {
      setFormError('Title is required.');
      return;
    }
    if (!url || !/^https?:\/\/.+/i.test(url)) {
      setFormError('Enter a valid URL (http or https).');
      return;
    }
    if (!category) {
      setFormError('Category is required.');
      return;
    }

    setSaving(true);
    try {
      const ref = await addDoc(collection(db, 'links'), {
        title,
        url,
        category,
        companyId,
        assignedUsers: [...assignedUserIds],
        createdAt: serverTimestamp(),
      });
      const uid = auth.currentUser?.uid;
      if (uid) {
        await appendTicketLog(db, {
          companyId,
          ticketId: null,
          action: 'external_link_created',
          summary: `External link added: ${title}`,
          meta: { linkId: ref.id, title, url },
          actorUid: uid,
          actorEmail: auth.currentUser?.email || '',
          actorRole: userData?.role || '',
          participantUids: [uid, ...assignedUserIds].filter(Boolean),
          resourceType: 'link',
        });
      }
      handleAddClose();
    } catch (err) {
      console.error('Add link error:', err);
      setFormError(err.message || 'Could not save link.');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      const uid = auth.currentUser?.uid;
      const title = deleteTarget.title || deleteTarget.id;
      if (uid && companyId) {
        await appendTicketLog(db, {
          companyId,
          ticketId: null,
          action: 'external_link_deleted',
          summary: `External link removed: ${title}`,
          meta: { linkId: deleteTarget.id, title },
          actorUid: uid,
          actorEmail: auth.currentUser?.email || '',
          actorRole: userData?.role || '',
          participantUids: [uid],
          resourceType: 'link',
        });
      }
      await deleteDoc(doc(db, 'links', deleteTarget.id));
      setDeleteTarget(null);
    } catch (err) {
      console.error('Delete link error:', err);
      setFetchError(err.message || 'Could not delete link.');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleCheckLinkNow = async (link) => {
    if (!link?.id) return;
    setCheckingLinkId(link.id);
    try {
      const runCheck = httpsCallable(functions, 'runExternalLinkLivenessNow');
      await runCheck({ linkId: link.id });
    } catch (err) {
      console.error('runExternalLinkLivenessNow:', err);
      setFetchError(err.message || 'Could not run liveness check.');
    } finally {
      setCheckingLinkId('');
    }
  };

  const handleToggleLinkLivenessAlerts = async (link, enabled) => {
    if (!link?.id || !isCompanyAdmin) return;
    try {
      await updateDoc(doc(db, 'links', link.id), { livenessAlertsEnabled: enabled });
    } catch (err) {
      console.error('toggle link liveness alerts:', err);
      setFetchError(err.message || 'Could not update alert setting.');
    }
  };

  if (!userData) {
    return (
      <Alert severity="warning">
        We could not load your profile. Try signing out and back in, or contact support.
      </Alert>
    );
  }

  if (!companyId) {
    return (
      <Alert severity="info">
        Your account does not have a <strong>companyId</strong> yet. External links are scoped per
        company; ask a super admin to assign you to a company before links appear here.
      </Alert>
    );
  }

  return (
    <Box>
      <LoadingLockScreen open={(loading && splashOnce) || saving || deleting} />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ sm: 'flex-start' }} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            External links
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 640 }}>
            Company tools and bookmarks. Links are private to your organization.
            {isCompanyAdmin
              ? ' As an admin, you see all links and can add or remove them. Assign agents so they appear in non-admin accounts.'
              : ' You only see links your admin assigned to you.'}
          </Typography>
        </Box>
        {isCompanyAdmin && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { resetAddForm(); setAddOpen(true); }} sx={{ flexShrink: 0 }}>
            Add link
          </Button>
        )}
      </Stack>

      {fetchError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setFetchError(null)}>
          {fetchError}
        </Alert>
      )}

      {loading && !splashOnce ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : !loading && !fetchError && grouped.length === 0 ? (
        <Typography color="text.secondary">
          No links yet.
          {isCompanyAdmin
            ? ' Use Add link to create the first one for your team.'
            : ' Ask your admin to assign links to your account.'}
        </Typography>
      ) : !loading && fetchError ? null : !loading ? (
        <Stack spacing={4}>
          {grouped.map(([category, categoryLinks]) => (
            <Box key={category}>
              <Typography variant="h6" component="h2" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}>
                {category}
              </Typography>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                {categoryLinks.map((link) => {
                  const n = getAssignedIds(link).length;
                  const hc = healthChip(link);
                  const checking = checkingLinkId === link.id;
                  return (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={link.id}>
                      <Card variant="outlined" sx={{ height: '100%', position: 'relative' }}>
                        {isCompanyAdmin && (
                          <Tooltip title={assignmentTooltip(link, companyUsers)} placement="top" enterTouchDelay={0}>
                            <Chip
                              size="small"
                              label={n === 0 ? 'Unassigned' : `${n} assigned`}
                              color={n === 0 ? 'default' : 'primary'}
                              variant={n === 0 ? 'outlined' : 'filled'}
                              sx={{
                                position: 'absolute',
                                top: 8,
                                left: 8,
                                zIndex: 1,
                                maxWidth: 'calc(100% - 88px)',
                                '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                            />
                          </Tooltip>
                        )}
                        <Stack
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                          sx={{ position: 'absolute', top: 8, right: isCompanyAdmin ? 36 : 8, zIndex: 1 }}
                        >
                          <Chip
                            size="small"
                            label={hc.label}
                            color={hc.color}
                            variant="outlined"
                          />
                          <Tooltip title={isCompanyAdmin ? 'Run liveness check now' : 'Only company admins can run checks'}>
                            <span>
                              <IconButton
                                size="small"
                                disabled={!isCompanyAdmin || checking}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleCheckLinkNow(link);
                                }}
                                sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}
                              >
                                {checking ? <CircularProgress size={14} /> : <AutorenewIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                        <CardActionArea
                          component="div"
                          role="link"
                          tabIndex={0}
                          onClick={() => openExternalLinkReuseOrFocus(link.id, link.url)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              openExternalLinkReuseOrFocus(link.id, link.url);
                            }
                          }}
                          sx={{
                            height: '100%',
                            alignItems: 'stretch',
                            pr: isCompanyAdmin ? 5 : 0,
                            pt: isCompanyAdmin ? 5 : 0,
                            pb: isCompanyAdmin ? 4 : 0,
                          }}
                        >
                          <CardContent sx={{ position: 'relative', pb: 6 }}>
                            <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle1" component="h3" noWrap title={link.title}>
                                  {link.title}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" noWrap title={link.url}>
                                  {link.url}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                  {link.lastCheckAt ? `Checked ${parseDate(link.lastCheckAt)?.toLocaleString() || '—'}` : 'Never checked'}
                                </Typography>
                                {isCompanyAdmin && (
                                  <FormControlLabel
                                    sx={{ mt: 0.5, ml: 0, display: 'flex', alignItems: 'center' }}
                                    control={(
                                      <Switch
                                        size="small"
                                        checked={link.livenessAlertsEnabled !== false}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          handleToggleLinkLivenessAlerts(link, e.target.checked);
                                        }}
                                      />
                                    )}
                                    label={(
                                      <Typography variant="caption" color="text.secondary">
                                        Liveness notifications
                                      </Typography>
                                    )}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                )}
                              </Box>
                              <Stack direction="row" spacing={0.25} alignItems="center" sx={{ flexShrink: 0, mt: 0.25 }}>
                                <OpenInNewIcon fontSize="small" color="action" />
                              </Stack>
                            </Stack>
                            <Tooltip title="Open in a new browser tab">
                              <IconButton
                                size="small"
                                aria-label={`New tab: ${link.title}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  openExternalLinkNewTab(link.url);
                                }}
                                sx={{
                                  position: 'absolute',
                                  bottom: 8,
                                  right: 8,
                                  zIndex: 2,
                                  bgcolor: 'background.paper',
                                  border: 1,
                                  borderColor: 'divider',
                                  boxShadow: 1,
                                }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </CardContent>
                        </CardActionArea>
                        {isCompanyAdmin && (
                          <IconButton
                            size="small"
                            aria-label={`Delete ${link.title}`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setDeleteTarget({ id: link.id, title: link.title });
                            }}
                            sx={{
                              position: 'absolute',
                              top: 4,
                              right: 4,
                              bgcolor: 'background.paper',
                              boxShadow: 1,
                              '&:hover': { bgcolor: 'action.hover' },
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" color="error" />
                          </IconButton>
                        )}
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          ))}
        </Stack>
      ) : null}

      <Dialog open={addOpen} onClose={handleAddClose} fullWidth maxWidth="sm">
        <DialogTitle>Add external link</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {formError && <Alert severity="error">{formError}</Alert>}
            <TextField
              label="Title"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              required
              fullWidth
              autoFocus
            />
            <TextField
              label="URL"
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              required
              fullWidth
              placeholder="https://example.com"
              helperText="Include https:// or we will add it for you."
            />
            <TextField
              label="Category"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              fullWidth
              placeholder="e.g. CRM, Finance, Back-Office"
              helperText="Links are grouped under this heading. Browser suggestions include common values."
              inputProps={{ list: 'external-link-categories' }}
            />
            <datalist id="external-link-categories">
              {CATEGORY_PRESETS.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>

            <FormControl fullWidth>
              <InputLabel id="external-links-assign-label">Assign to users</InputLabel>
              <Select
                labelId="external-links-assign-label"
                multiple
                value={assignedUserIds}
                onChange={(e) => {
                  const v = e.target.value;
                  setAssignedUserIds(typeof v === 'string' ? v.split(',') : v);
                }}
                input={<OutlinedInput label="Assign to users" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        None (admins only)
                      </Typography>
                    ) : (
                      selected.map((id) => {
                        const u = companyUsers.find((x) => x.uid === id);
                        return (
                          <Chip key={id} size="small" label={u?.displayName || u?.email || id} />
                        );
                      })
                    )}
                  </Box>
                )}
              >
                {companyUsers.length === 0 ? (
                  <MenuItem disabled>No users found for this company</MenuItem>
                ) : (
                  companyUsers.map((u) => (
                    <MenuItem key={u.uid} value={u.uid}>
                      <Checkbox checked={assignedUserIds.includes(u.uid)} />
                      <ListItemText primary={u.displayName || u.email || u.uid} secondary={u.email || u.role} />
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" display="block">
              Selected agents can open this link from their dashboard. Leave empty for admin-only links.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAddSubmit} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete link?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove <strong>{deleteTarget?.title}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExternalLinks;
